import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/hero";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback((next: number) => {
    setIndex((next + heroSlides.length) % heroSlides.length);
  }, []);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [paused]);

  return (
    <section
      className="relative w-full h-[420px] md:h-[600px] overflow-hidden bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {heroSlides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            title={`${slide.title} - 森泽佳奈最新动态`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/85 via-primary-950/55 to-primary-950/20"></div>
        </div>
      ))}

      <div className="relative z-10 h-full w-full px-4 md:px-8 flex items-center">
        <div className="max-w-2xl text-background-50">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500 text-primary-950 text-xs font-medium mb-4 animate-fade-up">
            <i className="ri-flashlight-fill"></i>
            {heroSlides[index].tag}
          </span>
          <h2
            key={heroSlides[index].id}
            className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold leading-tight animate-fade-up"
          >
            {heroSlides[index].title}
          </h2>
          <p
            key={`${heroSlides[index].id}-desc`}
            className="mt-4 text-sm md:text-lg text-background-200 leading-relaxed max-w-xl animate-fade-up"
          >
            {heroSlides[index].summary}
          </p>
          <div className="mt-6 md:mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to={`/detail/${heroSlides[index].id}`}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              阅读详情
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/works"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md border border-background-50/40 text-background-50 text-sm font-medium hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
            >
              浏览全部作品
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute z-10 bottom-5 md:bottom-8 left-0 right-0 px-4 md:px-8 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {heroSlides.map((slide, i) => (
            <button
              key={slide.id}
              type="button"
              aria-label={`切换到第 ${i + 1} 张`}
              onClick={() => go(i)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index ? "w-8 bg-accent-500" : "w-4 bg-background-50/50 hover:bg-background-50/80"
              }`}
            />
          ))}
        </div>
        <div className="hidden md:flex items-center gap-2">
          <button
            type="button"
            aria-label="上一张"
            onClick={() => go(index - 1)}
            className="w-10 h-10 flex items-center justify-center rounded-full border border-background-50/40 text-background-50 hover:bg-background-50/10 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
          </button>
          <button
            type="button"
            aria-label="下一张"
            onClick={() => go(index + 1)}
            className="w-10 h-10 flex items-center justify-center rounded-full border border-background-50/40 text-background-50 hover:bg-background-50/10 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </div>
    </section>
  );
}